/**
 * Live log stream. Screen spec S17.
 *
 * The log fence (LogFence) prints a log that has finished: a build log, the
 * whole thing, at rest. This is the other case, where lines are still
 * arriving, and the difference is not decoration. A stream has to say three
 * things a static log never has to say: this is the newest line, more are
 * coming, and you are looking at the end of it.
 *
 * THE CARET IS THE ONE SANCTIONED LOOP IN THE PRODUCT. Everywhere else, a
 * blinking caret is refused by name (screen spec 0.3 lists it as a Loop and HQ
 * motif SV Cloud does not wear), and the marketing page says so in its own
 * stylesheet. Here it earns its place because it is not a costume: it marks
 * the position the next line will land in, and it is the only thing on screen
 * that distinguishes a stream that is running from a stream that has stopped.
 * It is a 1px rule, not a block, it is the accent (a legal accent mount in the
 * direction files, "the log tail caret"), and it goes out the instant the
 * stream is paused. A caret that blinked while nothing was arriving would be
 * the exact lie this component exists to avoid.
 *
 * Nothing wraps. A wrapped log line misaligns everything under it, and a
 * person reading a tail is reading down a column of timestamps.
 *
 * The stream is not an aria live region. A tail that announced every line
 * would make a screen reader unusable inside two seconds, so the region is
 * named and focusable and reads on demand, and the thing that IS announced is
 * the state change: streaming, paused. That announcement belongs to the screen
 * that owns the pause control, not to the well.
 */

import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { t } from '../../lib/i18n/t';
import type { LiveLogLevel } from '../../lib/types';
import '../../styles/components/log-stream.css';

/** Three letters, mono, fixed width, and the word carries the level, not the
 *  colour. In greyscale the tail still reads. */
const TAG: Record<LiveLogLevel, string> = {
  app: 'app',
  request: 'req',
  error: 'err',
};

export interface StreamLine {
  id: string;
  /** A wall clock stamp, mono: 14:12:03. */
  at: string;
  level: LiveLogLevel;
  text: string;
}

export interface LogStreamProps {
  /** Names the region for a screen reader: "Live logs". */
  label: string;
  lines: StreamLine[];
  /** True while lines are still arriving. Drives the caret and the scroll. */
  live: boolean;
  /** Sticks to the newest line. A screen turns it off when a person scrolls. */
  follow?: boolean;
  /** What to say when there is nothing to print. Never an empty box. */
  quiet?: ReactNode;
}

export function LogStream({ label, lines, live, follow = true, quiet }: LogStreamProps) {
  const wellRef = useRef<HTMLDivElement | null>(null);
  const count = lines.length;

  // Following is a write to scrollTop rather than a scroll animation, so a
  // fast stream does not queue up hundreds of smooth scrolls and lag behind
  // the lines it is supposed to be showing.
  useEffect(() => {
    if (!follow) return;
    const well = wellRef.current;
    if (!well) return;
    well.scrollTop = well.scrollHeight;
  }, [follow, count]);

  return (
    <div
      className="lstream"
      role="region"
      aria-label={label}
      tabIndex={0}
      ref={wellRef}
      data-empty={count === 0 ? 'yes' : 'no'}
    >
      {count === 0 ? (
        <p className="lstream__quiet">{quiet ?? t('Nothing has come through yet.')}</p>
      ) : (
        <div className="lstream__grid mono">
          {lines.map((line) => (
            <Fragment key={line.id}>
              <span className="lstream__at">{line.at}</span>
              <span className="lstream__tag" data-level={line.level}>
                {TAG[line.level]}
              </span>
              <span className="lstream__line" data-level={line.level}>
                {line.text}
              </span>
            </Fragment>
          ))}

          <span className="lstream__at" aria-hidden="true" />
          <span className="lstream__tag" aria-hidden="true" />
          <span className="lstream__caret" data-on={live ? 'yes' : 'no'} aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
